import type { SurfaceRecipeId } from "@vjal/screen-ir";
import { createContext, useContext, type ComponentType, type ReactNode } from "react";
import type { RouteAccessDecision } from "../domain/capabilities";
import type { CoachingRole } from "../domain/roles";
import { ProductRoutePage } from "./ProductRoutePage";

export type ProductRouteRenderContextValue = {
  role: CoachingRole;
  decision: RouteAccessDecision;
  routePath: string;
  stateOverride?: ReactNode;
  recipe?: SurfaceRecipeId;
};

const ProductRouteRenderContext = createContext<ProductRouteRenderContextValue | null>(null);

export function ProductRouteRenderProvider({
  value,
  children,
}: {
  value: ProductRouteRenderContextValue;
  children: ReactNode;
}) {
  return (
    <ProductRouteRenderContext.Provider value={value}>{children}</ProductRouteRenderContext.Provider>
  );
}

export function useProductRouteRenderContext(): ProductRouteRenderContextValue {
  const value = useContext(ProductRouteRenderContext);
  if (!value) {
    throw new Error("ProductRouteScreenAdapter requires ProductRouteRenderProvider");
  }
  return value;
}

/** Shared page-backed ScreenModule body — one product authority for all coaching routes. */
export function ProductRouteScreenAdapter() {
  const { role, decision, routePath, stateOverride, recipe } = useProductRouteRenderContext();
  return (
    <ProductRoutePage
      role={role}
      decision={decision}
      routePath={routePath}
      stateOverride={stateOverride}
      recipe={recipe}
    />
  );
}

export type ProductRouteScreenAdapterType = ComponentType;
